import { readArray, readFiniteNumber } from "./viewTimeSeries";

function isObject(value) {
  return value && typeof value === "object" && !Array.isArray(value);
}

function normalizeStat(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalized === "median" ? "median" : "mean";
}

function isFlowLoopRecord(value) {
  if (!isObject(value)) return false;

  return (
    Array.isArray(value?.breaths) ||
    isObject(value?.averaged_loop) ||
    isObject(value?.averagedLoop) ||
    isObject(value?.summary_metrics) ||
    isObject(value?.summaryMetrics)
  );
}

export function buildFlowLoopPointsFromTraces(volumeTrace, flowTrace) {
  const volumes = readArray(volumeTrace);
  const flows = readArray(flowTrace);
  const pointCount = Math.min(volumes.length, flows.length);
  const points = [];

  for (let index = 0; index < pointCount; index += 1) {
    const x = readFiniteNumber(volumes[index]);
    const y = readFiniteNumber(flows[index]);

    if (x == null || y == null) {
      continue;
    }

    points.push({ x, y });
  }

  return points;
}

export function pickPrimaryFlowLoopRecord(source) {
  if (isFlowLoopRecord(source)) {
    return source;
  }

  return (
    source?.results?.mpm?.flow_loop ||
    source?.results?.pca?.flow_loop ||
    source?.results?.mpm?.flowLoop ||
    source?.results?.pca?.flowLoop ||
    null
  );
}

function normalizePooledAveragedLoop(averagedLoop) {
  const points = buildFlowLoopPointsFromTraces(
    averagedLoop?.meanLoop?.volumeTrace,
    averagedLoop?.meanLoop?.flowTrace,
  );

  if (points.length < 2) {
    return null;
  }

  return {
    mode: "pooled",
    stat: normalizeStat(averagedLoop?.stat),
    loopCount: readFiniteNumber(averagedLoop?.loopCount),
    limbPoints: readFiniteNumber(averagedLoop?.limbPoints),
    points,
    plotStorageUri:
      typeof averagedLoop?.plot_storage_uri === "string"
        ? averagedLoop.plot_storage_uri
        : null,
  };
}

function normalizeWindowedAveragedLoop(averagedLoop) {
  const windows = readArray(averagedLoop?.windows)
    .map((windowEntry, index) => {
      const points = buildFlowLoopPointsFromTraces(
        windowEntry?.meanLoop?.volumeTrace,
        windowEntry?.meanLoop?.flowTrace,
      );

      if (points.length < 2) {
        return null;
      }

      const windowIndex =
        readFiniteNumber(windowEntry?.index) ?? index;

      return {
        index: windowIndex,
        centerTimeSeconds: readFiniteNumber(windowEntry?.centerTimeSeconds),
        breathCount: readFiniteNumber(windowEntry?.breathCount),
        points,
      };
    })
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = left?.centerTimeSeconds;
      const rightTime = right?.centerTimeSeconds;

      if (leftTime != null && rightTime != null && leftTime !== rightTime) {
        return leftTime - rightTime;
      }

      return (left?.index ?? 0) - (right?.index ?? 0);
    });

  if (!windows.length) {
    return null;
  }

  return {
    mode: "windowed",
    stat: normalizeStat(averagedLoop?.stat),
    window: readFiniteNumber(averagedLoop?.window),
    step: readFiniteNumber(averagedLoop?.step),
    windowCount: readFiniteNumber(averagedLoop?.windowCount) ?? windows.length,
    limbPoints: readFiniteNumber(averagedLoop?.limbPoints),
    windows,
    plotStorageUri:
      typeof averagedLoop?.plot_storage_uri === "string"
        ? averagedLoop.plot_storage_uri
        : null,
  };
}

export function pickAveragedFlowLoop(source) {
  const flowLoopRecord = pickPrimaryFlowLoopRecord(source);
  const averagedLoop =
    flowLoopRecord?.averaged_loop || flowLoopRecord?.averagedLoop || null;

  if (!isObject(averagedLoop)) {
    return null;
  }

  if (averagedLoop?.mode === "windowed") {
    return normalizeWindowedAveragedLoop(averagedLoop);
  }

  return normalizePooledAveragedLoop(averagedLoop);
}

export function hasFirestoreFlowLoops(source) {
  return Array.isArray(pickPrimaryFlowLoopRecord(source)?.breaths);
}

export function hasAveragedFlowLoop(source) {
  return pickAveragedFlowLoop(source) != null;
}
